import React, { useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import PasswordInput from '../components/PasswordInput';

export default function SignUp() {
  const { session } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigateTo = useNavigate();

  if (session) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { first_name: firstName, last_name: lastName },
        },
      });

      if (signUpError) throw signUpError;

      // Create profile, clinic, and link them (all via backend with service role key)
      if (data.user) {
        const clinic = await api.settings.createClinic(clinicName, email, firstName, lastName);
        navigateTo(`/choose-plan?clinicId=${clinic.id}`);
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="flex-1 flex items-center justify-center bg-white px-6 py-12 lg:py-0 lg:w-1/2">
          <div className="w-full max-w-[400px] text-center">
            <img src="/cscore-logo.png" alt="CScore" className="h-10 mx-auto mb-6" style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }} />
            <h2 className="text-[28px] font-semibold text-text mb-4">Check Your Email</h2>
            <p className="text-sm text-gray-600 mb-6">
              We've sent a confirmation link to <strong>{email}</strong>. Click the link to activate your account.
            </p>
            <Link to="/login" className="text-primary font-medium text-sm hover:underline">
              Back to Sign In
            </Link>
          </div>
        </div>
        <div
          className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center px-12"
          style={{ background: 'radial-gradient(ellipse farthest-corner at 10% 10%, #7F31DB, #E1C2FA)' }}
        >
          <h2 className="text-white text-4xl font-bold mb-4 text-center">Measure What Matters</h2>
          <p className="text-white/80 text-lg text-center max-w-md leading-relaxed">
            Track clinician performance, improve patient outcomes, and make data-driven decisions for your treatment center.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - form */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12 lg:py-0 lg:w-1/2">
        <div className="w-full max-w-[400px]">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src="/cscore-logo.png" alt="CScore" className="h-10 mx-auto" style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }} />
          </div>

          <h2 className="text-[28px] font-semibold text-text text-center mb-8">Sign Up</h2>

          <form onSubmit={handleSubmit}>
            <div className="flex gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-text mb-1.5">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-text mb-1.5">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1.5">Clinic Name</label>
              <input
                type="text"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                required
                placeholder="e.g. Topsail Recovery"
                className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1.5">Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-text mb-1.5">Confirm Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#CB9EF6' }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#B279F0')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#CB9EF6')}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>

            <p className="text-sm text-gray-600 mt-4 text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">Sign In</Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right side - gradient (hidden on mobile) */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center px-12"
        style={{ background: 'radial-gradient(ellipse farthest-corner at 10% 10%, #7F31DB, #E1C2FA)' }}
      >
        <h2 className="text-white text-4xl font-bold mb-4 text-center">Measure What Matters</h2>
        <p className="text-white/80 text-lg text-center max-w-md leading-relaxed">
          Track clinician performance, improve patient outcomes, and make data-driven decisions for your treatment center.
        </p>
      </div>
    </div>
  );
}
